/**
 * App-wide toast notifications. Any module may call `toast.success(...)` etc.;
 * <Toaster/> (components/Toast.tsx, rendered once by the shell) shows them in
 * an aria-live region.
 */
import { useSyncExternalStore } from 'react';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  action?: ToastAction;
  /** Milliseconds; 0 = until dismissed. */
  duration: number;
}

const DEFAULT_DURATION: Record<ToastKind, number> = { info: 4000, success: 3000, warning: 6000, error: 8000 };
const MAX_VISIBLE = 4;

let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit(): void {
  for (const l of listeners) l();
}

function dismissTimer(id: number): void {
  const timer = timers.get(id);
  if (timer) clearTimeout(timer);
  timers.delete(id);
}

function arm(id: number, duration: number): void {
  dismissTimer(id);
  if (duration > 0) timers.set(id, setTimeout(() => dismissToast(id), duration));
}

export function dismissToast(id: number): void {
  dismissTimer(id);
  const next = items.filter((t) => t.id !== id);
  if (next.length !== items.length) {
    items = next;
    emit();
  }
}

/**
 * Shows a toast. A message that is already visible (same kind, no action) is
 * not repeated but stays up longer, so a burst of identical failures (e.g.
 * several requests failing at once) shows a single toast.
 */
export function showToast(kind: ToastKind, message: string, opts: { action?: ToastAction; duration?: number } = {}): number {
  const duration = opts.duration ?? DEFAULT_DURATION[kind];
  const same = opts.action ? undefined : items.find((t) => t.kind === kind && t.message === message && !t.action);
  if (same) {
    if (same.duration > 0) arm(same.id, duration);
    return same.id;
  }
  const item: ToastItem = { id: nextId++, kind, message, duration };
  if (opts.action) item.action = opts.action;
  for (const dropped of items.slice(0, Math.max(0, items.length + 1 - MAX_VISIBLE))) dismissTimer(dropped.id);
  items = [...items, item].slice(-MAX_VISIBLE);
  arm(item.id, duration);
  emit();
  return item.id;
}

/** Removes every toast (e.g. when the session ended: nothing of it may linger). */
export function clearToasts(): void {
  for (const id of timers.keys()) dismissTimer(id);
  if (items.length === 0) return;
  items = [];
  emit();
}

export const toast = {
  info: (message: string, opts?: { action?: ToastAction; duration?: number }) => showToast('info', message, opts),
  success: (message: string, opts?: { action?: ToastAction; duration?: number }) => showToast('success', message, opts),
  warning: (message: string, opts?: { action?: ToastAction; duration?: number }) => showToast('warning', message, opts),
  error: (message: string, opts?: { action?: ToastAction; duration?: number }) => showToast('error', message, opts),
};

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const getItems = () => items;

export function useToasts(): ToastItem[] {
  return useSyncExternalStore(subscribe, getItems, getItems);
}
